/**
 * Branding multi-tenant (7barber platform).
 *
 * Distincao importante:
 *
 * - PLATFORM_*  - Branding global da plataforma 7barber. Sempre o mesmo
 *                 em todas as deploys. Usado em landing, login, register,
 *                 painel super-admin, footer global, emails sistema, etc.
 *
 * - getTenantBranding(tenant) - Resolve branding por-tenant com fallback
 *                 pros defaults da plataforma. Usado em dashboards do tenant
 *                 logado, booking publico, emails transacionais do tenant.
 *
 * - TENANT_* (deprecated) - Aliases de compatibilidade pros consumers atuais
 *                 (login.tsx, register.tsx, Dashboard{Top,Side}bar.tsx). Apontam
 *                 pros defaults da plataforma. Sera migrado na Fase 2 quando
 *                 o tenant context (Server Component) estiver disponivel.
 */

import type { Tenant } from './api';
import { PLATFORM_DOMAIN, PLATFORM_HOST } from './platform-domain';

export { PLATFORM_DOMAIN, PLATFORM_HOST };

// =====================================================================
// PLATAFORMA - constantes globais (sempre 7barber)
// =====================================================================

export const PLATFORM_NAME = '7barber';

export const PLATFORM_SHORT_NAME = '7barber';

export const PLATFORM_TAGLINE = 'Plataforma de gestão para barbearias';

/** Logo "extenso" da plataforma. Por enquanto aponta pro mesmo SVG do
 *  icone (so o "7b"); no futuro pode virar um logo horizontal com texto.
 *  Usar em metadata OG/Twitter, e-mails marketing, footers grandes. */
export const PLATFORM_LOGO_URL = '/logo-7barber.svg';

/** Icone compacto da plataforma (favicon, tile/avatar pequeno em cards
 *  de auth, badge no menu, etc). Hoje e o mesmo SVG do logo, mas separado
 *  pra que possamos divergir sem quebrar consumers - ex: trocar por um
 *  logo horizontal em PLATFORM_LOGO_URL e manter o "7b" aqui. */
export const PLATFORM_ICON_URL = '/logo-7barber.svg';

export const PLATFORM_FAVICON_URL = '/favicon.ico';

/** Cor primaria default (mesma de globals.css --brand). Tenants podem override. */
export const PLATFORM_PRIMARY_COLOR = '#5F2EE0';

/** URL canonica da plataforma raiz (landing/admin/auth). */
export const PLATFORM_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? `https://${PLATFORM_HOST}`;

/**
 * Identidade juridica da pessoa que opera a plataforma. Usada em dados
 * estruturados (JSON-LD Organization) e em security.txt para que os robôs
 * de classificacao de URL dos filtros corporativos (Netskope, Palo Alto,
 * Fortinet, etc.) reconhecam o dominio como negocio legitimo (categoria
 * Business) - e nao "uncategorized". Dados do cartao CNPJ da Receita.
 * Identidade global da plataforma (mesma em toda deploy), por isso vive
 * aqui junto de PLATFORM_NAME e nao no SSOT por-tenant.
 */
export const PLATFORM_ORG = {
  legalName: '7CORS CONSULTORIA EM TECNOLOGIA DA INFORMACAO LTDA',
  /** CNPJ (tax id). */
  taxId: '52.032.587/0001-14',
  email: 'admin@7cors.com.br',
  telephone: '+55 85 9965-1360',
  /** Site institucional da operadora (dominio antigo, ja confiavel/categorizado).
   *  Vira `sameAs` no JSON-LD para o 7barber.app herdar reputacao via grafo. */
  corporateSite: 'https://7cors.com.br',
  address: {
    streetAddress: 'Rua Valdizar Saldanha Fontenele, 71',
    addressLocality: 'Fortaleza',
    addressRegion: 'CE',
    postalCode: '60831-460',
    addressCountry: 'BR',
  },
} as const;

// =====================================================================
// HELPERS por-tenant
// =====================================================================

export interface TenantBranding {
  /** Nome completo a exibir (ex: "Barbearia do Jose"). Fallback: PLATFORM_NAME. */
  name: string;
  /** Nome curto para sidebars/topbars. Fallback: PLATFORM_SHORT_NAME. */
  shortName: string;
  /** URL do logo. Fallback: PLATFORM_LOGO_URL. */
  logoUrl: string;
  /** Cor primaria da marca. Fallback: PLATFORM_PRIMARY_COLOR. */
  primaryColor: string;
  /** URL canonica do tenant (subdominio). undefined se sem tenant. */
  url?: string;
}

/**
 * Resolve branding pra exibicao em UI/emails/og.
 *
 * - Sem tenant (ex: pagina publica de marketing/admin): retorna PLATFORM defaults
 * - Com tenant: aplica overrides do tenant onde houver, fallback pra PLATFORM
 */
export function getTenantBranding(tenant?: Tenant | null): TenantBranding {
  if (!tenant) {
    return {
      name: PLATFORM_NAME,
      shortName: PLATFORM_SHORT_NAME,
      logoUrl: PLATFORM_LOGO_URL,
      primaryColor: PLATFORM_PRIMARY_COLOR,
    };
  }

  return {
    name: tenant.name,
    shortName: tenant.name,
    logoUrl: tenant.logoUrl ?? tenant.logo ?? PLATFORM_LOGO_URL,
    primaryColor: tenant.settings?.theme?.brand ?? PLATFORM_PRIMARY_COLOR,
    url: `https://${tenant.slug}.${PLATFORM_DOMAIN}`,
  };
}

/**
 * Resolve o dominio raiz da plataforma em runtime.
 *
 * Prioridade (do mais autoritativo pro menos):
 *
 * 1) `window.location.hostname` - **fonte de verdade no browser**. Reflete
 *    o dominio onde o usuario REALMENTE esta. Bundle antigo embutido com
 *    env desatualizada nao falsifica esta resposta. Critico durante cutover
 *    de dominio (ADR-019): super-admin operando em `7barber.app/admin`
 *    via bundle antigo (NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN=7cors.com.br)
 *    ainda monta `<slug>.7barber.app` corretamente porque o hostname e
 *    `7barber.app`.
 *
 * 2) `process.env.NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN` - fallback para SSR
 *    (sem window) e build-time. SSOT real e PlatformConfig.rootDomain
 *    no banco; o CI/CD le do banco e injeta no build.
 *
 * 3) `PLATFORM_DOMAIN` constante - ultimo recurso (default do bootstrap).
 */
function getRuntimePlatformDomain(slug?: string): string {
  if (typeof window !== 'undefined') {
    // `host` (e nao `hostname`) preserva a porta - sem isso o link de tenant
    // gerado em dev (localhost:3000) aponta pra porta 80 e quebra.
    const host = window.location.host;
    if (slug && host.startsWith(`${slug}.`)) {
      return host.slice(slug.length + 1);
    }
    return host;
  }
  const configured = process.env.NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN?.trim();
  if (configured) return configured;
  return PLATFORM_DOMAIN;
}

function getRuntimeProtocol(): string {
  if (typeof window === 'undefined') return 'https:';
  return window.location.protocol;
}

export function buildTenantUrl(slug: string, path = ''): string {
  const domain = getRuntimePlatformDomain(slug);
  const origin = `${getRuntimeProtocol()}//${slug}.${domain}`;
  if (!path) return origin;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalizedPath}`;
}

// =====================================================================
// COMPAT (deprecated - sera removido na Fase 2 quando tenant context existir)
// =====================================================================

/** @deprecated use PLATFORM_LOGO_URL ou getTenantBranding(tenant).logoUrl */
export const TENANT_LOGO_URL = PLATFORM_LOGO_URL;

/** @deprecated use PLATFORM_NAME ou getTenantBranding(tenant).name */
export const TENANT_NAME = PLATFORM_NAME;

/** @deprecated use PLATFORM_SHORT_NAME ou getTenantBranding(tenant).shortName */
export const TENANT_SHORT_NAME = PLATFORM_SHORT_NAME;

/** @deprecated use tenant.slug do contexto (Fase 2) */
export const TENANT_SLUG =
  process.env.NEXT_PUBLIC_TENANT_SLUG ?? 'platform';

/** @deprecated use PLATFORM_URL ou getTenantBranding(tenant).url */
export const SITE_URL = PLATFORM_URL;
